import React from "react";
import Head from "next/head";
import Image from "next/image";
// import Link from "next/link";
import CtaOne from "@components/CTA/CtaOne";
import Legal from "components/SignatureLtd/Legal";
import { BASE_ASSETS_URL } from "@constants/constants";
import { TbExternalLink } from "react-icons/tb";
import FaqCard from "@components/Cards/FaqCard";
import SignUpForm from "@components/Contracts/SignUpForm";
import FAQ2 from "components/CLM/FAQ2";

const features = [
  {
    image: "1.png",
    title: "Sign Documents Anytime, Anywhere",
    description: `BoloSign gives you the ability to let your clients sign documents from anywhere; they might be. This platform is so convenient for workers working remotely on simple or complex businesses. The platform is easy to use for every party involved—No more hassle just quick and hassle-free signing!`,
  },
  {
    image: "2.png",
    title: "Affordable Plans for Every Business",
    description: `We understand that small businesses search for services that are less expensive. That is why BoloSign includes <a href="https://www.boloforms.com/signature/pricing/" class="underline font-semibold">plans</a> that are within your budget yet with the features you need. You can easily upload, send, and electronically sign documents in your team, regardless of its size.`,
  },
  {
    image: "3.png",
    title: "Secure and Legally-Binding Signatures",
    description: `<a href="https://security.boloforms.com/" class="underline font-semibold">Security</a> is an important factor in the world today. BoloSign is the solution offering a legally binding eSign option and it complies with <a href="https://www.boloforms.com/signature/electric-signature-legality/" class="underline font-semibold">ESIGN and UETA laws.</a> You will get a certificate of authentication for every document you send, which will give you peace of mind. Besides, you can add extra protection by asking recipients for a password. All your confidential data is safe with us!`,
  },
  {
    image: "4.png",
    title: "Collaborate with your Team",
    description: `Collaborate effortlessly with your team using BoloSign – streamline document workflows, share, sign, and manage all in one place. Experience the best of free eSignatures with BoloSign – secure, simple, and perfect for every business need.`,
  },
  {
    image: "5.png",
    title: "Get Paid Faster",
    description: `Getting a document signed is good but having the document signed and paid for is way better! Once a document is signed with BoloSign, customers can complete the payment procedure immediately through our Stripe integration. It is a hassle-free process that will improve your cash flow with quicker returns.`,
  },
  {
    image: "6.png",
    title: "Easy Document Management",
    description: `No more jumping between apps to get your documents signed. With BoloSign, you can upload and sign PDFs, Word documents, Excel files, and even JPEGs—all in one place. It’s a flexible and affordable eSignature tool, designed especially for small businesses. Try BoloSign today and see how easy document signing can be!`,
  },
];

const FaqData = [
  {
    question: "What is BoloSign’s Free eSignature Software?",
    answer:
      "BoloSign’s Free eSignature Software is a secure, legally binding electronic signature solution that allows you to sign documents anytime, anywhere. With our platform, you can quickly and easily get documents signed, collaborate with your team, and manage your documents efficiently, all for free!",
  },
  {
    question: "How secure are electronic signatures with BoloSign?",
    answer:
      "BoloSign ensures your electronic signatures are secure and legally binding by complying with ESIGN and UETA laws. Each signed document comes with a certificate of authentication, and you can add extra protection with password access, ensuring your confidential data remains safe.",
  },
  {
    question: "Can I collaborate with my team using BoloSign?",
    answer:
      "Yes, BoloSign makes team collaboration simple by allowing you to share, review, and sign documents together in one place. Our platform streamlines the document workflow for teams of all sizes, making it ideal for small businesses and remote work environments.",
  },
  {
    question:
      "Is there a free trial available for BoloSign’s eSignature service?",
    answer:
      "Yes, BoloSign offers a 7-day free trial of our eSignature service, which includes access to all features of the Business Plan. You can explore our platform at your own pace, with no commitment, and experience how it meets your eSignature needs.",
  },
];

const ElectronicSignature = () => {
  // const router = useRouter();
  // const context = useContext(AppContext);
  return (
    <div className="w-full md:mt-20 mt-8">
      <Head>
        <meta charSet="utf-8" />
        <title>What is an electronic signature? | BoloForms</title>
        <meta
          name="description"
          content="Electronic signatures refer to signatures in digital format, distinguishing them from traditional handwritten signatures. These digital signatures hold full legal validity for indicating consent or endorsement of document contents. The prevalence of eSignatures is progressively supplanting manual signatures, serving as a versatile method to sign a wide array of documents spanning various sectors on a global scale."
        />
        <meta name="geo.region" content="IN" />
        <meta name="geo.position" content="22.351115;78.667743" />
        <meta name="ICBM" content="22.351115, 78.667743" />
        <meta name="author" content="Boloforms" />
        <meta name="publisher" content="BoloForms" />
        <meta
          name="keywords"
          content="eSignature, electronic signature, digital signature, eSign, eSignatures, electronic signatures, digital signatures, eSignature software, electronic signature software, digital signature software, eSignature solution, electronic signature solution, digital signature solution, eSignature app, electronic signature app, digital signature app, eSignature platform, electronic signature platform, digital signature platform, eSignature service, electronic signature service, digital signature service, eSignature tool, electronic signature tool, digital signature tool, eSignature online, electronic signature online, digital signature online, eSignature solution online, electronic signature solution online, digital signature solution online, eSignature software online, electronic signature software online, digital signature software online, eSignature platform online, electronic signature platform online, digital signature platform online"
        />
        <meta
          property="og:url"
          content="https://www.bolosign.com/signature/what-is-electronic-signature/"
        />
        <meta property="og:type" content="website" />
        <meta
          property="og:title"
          content="What is an electronic signature? | BoloForms"
        />
        <meta name="twitter:site" content="@boloforms" />
        <meta
          property="twitter:url"
          content="https://www.bolosign.com/signature/what-is-electronic-signature/"
        />
        <meta
          name="twitter:title"
          content="What is an electronic signature? | BoloForms"
        />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:creator" content="@boloforms" />
        <meta property="twitter:domain" content="https://bolosign.com" />
        <meta name="twitter:description" content="" />
        <meta property="og:description" content="" />
        <meta
          name="robots"
          content="INDEX, FOLLOW, MAX-IMAGE-PREVIEW:LARGE, MAX-SNIPPET:-1, MAX-VIDEO-PREVIEW:-1"
        />
        <link
          rel="canonical"
          href="https://www.bolosign.com/signature/what-is-electronic-signature/"
        />
      </Head>
      <div className="w-full flex flex-col gap-24 ">
        {/* hero section  */}

        <div className="w-full max-w-screen-xl mx-auto flex flex-col md:flex-row justify-between items-center gap-12 md:gap-20  md:px-8 px-4">
          <div className="flex-1">
            <CtaOne
              heading2={"Free Electronic Signature"}
              paragraph={
                "Experience the best in secure and legally binding eSignatures with BoloSign, the ultimate free solution for small businesses. Try BoloSign's Free eSignature today and transform your document signing process!"
              }
              buttonPrimaryText={"Start For Free"}
              buttonPrimaryUrl={""}
              buttonPrimaryIcon={<TbExternalLink />}
              buttonPrimaryBg={"bg-black"}
              buttonPrimaryBorderShadow={false}
            />
          </div>
          <div className="flex-1 w-full">
            <Image
              src={require(
                `@assets/signature/what-is-electronic-signature/what-is-electronic-signature.gif`,
              )}
              // src={`${BASE_ASSETS_URL}/v2/gsuite/${image}`}
              className="w-full h-auto"
              width={50}
              height={50}
              alt="hero"
              priority
            />
          </div>
        </div>

        {/* features  */}
        <div className="w-full max-w-screen-mxl mx-auto flex flex-col justify-center items-center sm:gap-12 px-4 lg:px-8">
          {/* <div className="max-w-screen-md mx-auto text-center">
            <CtaOne heading2={heading} paragraph={description} />
          </div> */}
          <div className="w-full flex flex-col gap-12 sm:gap-20">
            <div className="w-full flex flex-col gap-16">
              {features?.map((feature, index) => (
                <div
                  key={index}
                  className={`w-full flex flex-col justify-between items-center gap-4 sm:gap-20 ${
                    index % 2 === 0 ? "sm:flex-row-reverse" : "sm:flex-row"
                  }`}
                >
                  <div className="w-full flex flex-col gap-4 sm:gap-6">
                    {feature.title && (
                      <h1 className="text-2xl sm:text-4xl lg:text-5xl">
                        {feature.title}
                      </h1>
                    )}
                    <p
                      className="text-lg sm:text-xl lg:text-2xl"
                      dangerouslySetInnerHTML={{ __html: feature.description }}
                    ></p>
                  </div>
                  {feature.image && (
                    <div className="w-full">
                      <Image
                        src={require(
                          `@assets/signature/what-is-electronic-signature/${feature.image}`,
                        )}
                        // src={`${BASE_ASSETS_URL}/v2/gsuite/${feature.image}`}
                        className="w-full h-auto"
                        width={10}
                        height={10}
                        loading="lazy"
                        alt=""
                      />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        <section className="w-full bg-surface-light py-16 sm:py-24 border-y-2 border-surface-dark">
          <div className="w-full flex flex-col md:flex-row items-center gap-10 max-w-screen-xl mx-auto px-4 lg:px-8">
            <div className="w-full flex flex-col gap-6">
              <h1 className="max-w-screen-lg mx-auto text-3xl sm:text-4xl">
                Explore the Features with a Free 7-Day Trial
              </h1>
              <div className="">
                <SignUpForm phone={false} />
              </div>
              <p className="text-lg sm:text-xl">
                {`Enroll to our 7-day free trial now and check out why so many small business entities rely on BoloSign to cover their eSignature requirements.`}
              </p>
            </div>
            <div className="w-full">
              <Image
                src={require(`@assets/signature/contracts/free-trial.png`)}
                className="w-full h-auto"
                alt=""
                width={10}
                height={10}
              />
            </div>
          </div>
        </section>

        <div className="w-full max-w-screen-xl mx-auto px-4 lg:px-8">
          <Legal />
        </div>
        <section className="bg-[#f4f2f0] text-black py-16 md:px-10 px-4 xl:px-20 text-center md:pt-24 pt-12 ">
          <FAQ2 faqData={FaqData} heading={"Frequently Asked Questions"} />
        </section>
      </div>
    </div>
  );
};

export default ElectronicSignature;
